'use client';

/**
 * MobileMenu.tsx — hamburger og skuff for sm/md (ibm.com mobilmeny)
 *
 * Trinnvis navigasjon som på ibm.com: nivå 1 (toppvalg), nivå 2 (kategorier,
 * «‹ Tilbake» + tittel), nivå 3 (elementer). Skuffen er en modal dialog under
 * headeren: fokus flyttes inn ved åpning og ved trinnbytte, Tab er låst til
 * skuffen + hamburgeren, Escape lukker og returnerer fokus til hamburgeren.
 * Lukkes også ved navigasjon, klikk på lenke og når bredden går over lg.
 * Innholdet (MastheadMenu) kommer fra masthead-content.ts.
 */

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, ChevronLeft, ChevronRight, Close, Menu } from '@carbon/icons-react';
import type { MastheadMenu } from '@/types/landing';
import { LANDING_ICONS } from './landing-icons';
import styles from './mobile-menu.module.css';

interface Step {
  entryId?: string;
  catId?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled])';

export default function MobileMenu({ menu, navLabel }: { menu: MastheadMenu; navLabel: string }) {
  const pathname = usePathname();
  const uid = useId();
  const burgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  // Åpen skuff er knyttet til siden den ble åpnet på: navigasjon lukker den uten effekt
  const [state, setState] = useState<{ open: boolean; path: string }>({ open: false, path: '' });
  const open = state.open && state.path === pathname;
  const [step, setStep] = useState<Step>({});

  const close = useCallback(
    (returnFocus = false) => {
      setState({ open: false, path: pathname });
      setStep({});
      if (returnFocus) burgerRef.current?.focus();
    },
    [pathname],
  );

  const openMenu = () => {
    setStep({});
    setState({ open: true, path: pathname });
  };

  // Lukk når skjermen blir bred nok til desktop-menyen

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1056px)');
    const onChange = () => {
      if (mq.matches) close();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [close]);

  // Fokus inn i skuffen ved åpning og ved trinnbytte
  useEffect(() => {
    if (!open) return;
    drawerRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [open, step]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key !== 'Tab') return;
    // Fokusfelle: skuffen + hamburgeren (lukkeknappen)
    const nodes = [burgerRef.current, ...Array.from(drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(
      (n): n is HTMLElement => !!n,
    );
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const entry = menu.entries.find((x) => x.id === step.entryId);
  const mega = entry && entry.kind === 'mega' ? entry : undefined;
  const category = mega?.categories.find((c) => c.id === step.catId);

  const back = () => setStep(category ? { entryId: step.entryId } : {});

  return (
    <div onKeyDown={open ? onKeyDown : undefined}>
      <button
        type="button"
        ref={burgerRef}
        className={`${styles.burger} ${open ? styles.burgerOpen : ''}`}
        aria-expanded={open}
        aria-controls={`${uid}-drawer`}
        aria-label={open ? menu.labels.closeMenu : menu.labels.openMenu}
        onClick={() => (open ? close() : openMenu())}
      >
        {open ? <Close size={20} aria-hidden /> : <Menu size={20} aria-hidden />}
      </button>

      {open && (
        <div
          id={`${uid}-drawer`}
          ref={drawerRef}
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-label={menu.labels.mobileMenu}
        >
          <nav aria-label={navLabel}>
            {/* Nivå 1: toppvalg */}
            {!mega && (
              <ul className={styles.list}>
                {menu.entries.map((e, i) => (
                  <li key={e.id}>
                    {e.kind === 'link' ? (
                      <Link
                        href={e.href}
                        className={styles.row}
                        data-autofocus={i === 0 ? '' : undefined}
                        onClick={() => close()}
                      >
                        {e.label}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={styles.row}
                        data-autofocus={i === 0 ? '' : undefined}
                        onClick={() => setStep({ entryId: e.id })}
                      >
                        <span>{e.label}</span>
                        <ChevronRight size={16} aria-hidden />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Nivå 2: kategorier */}
            {mega && !category && (
              <>
                <button type="button" className={styles.backRow} data-autofocus="" onClick={back}>
                  <ChevronLeft size={16} aria-hidden />
                  {menu.labels.back}
                </button>
                <p className={styles.title}>{mega.label}</p>
                <ul className={styles.list}>
                  {mega.categories.map((c) => (
                    <li key={c.id}>
                      <button type="button" className={styles.row} onClick={() => setStep({ entryId: mega.id, catId: c.id })}>
                        <span>{c.label}</span>
                        <ChevronRight size={16} aria-hidden />
                      </button>
                    </li>
                  ))}
                  <li>
                    <Link href={mega.all.href} className={`${styles.row} ${styles.rowLink}`} onClick={() => close()}>
                      <span>{mega.all.label}</span>
                      <ArrowRight size={16} aria-hidden />
                    </Link>
                  </li>
                </ul>
              </>
            )}

            {/* Nivå 3: elementer */}
            {mega && category && (
              <>
                <button type="button" className={styles.backRow} data-autofocus="" onClick={back}>
                  <ChevronLeft size={16} aria-hidden />
                  {menu.labels.back}
                </button>
                <p className={styles.title}>{category.heading}</p>
                <ul className={styles.list}>
                  {category.items.map((item) => {
                    const Icon = LANDING_ICONS[item.icon];
                    return (
                      <li key={item.href + item.label}>
                        <Link href={item.href} className={`${styles.row} ${styles.rowItem}`} onClick={() => close()}>
                          <Icon className={styles.itemIcon} aria-hidden />
                          <span className={styles.itemText}>
                            <span className={styles.itemTitle}>
                              {item.label}
                              {item.status && <span className={styles.tag}>{item.status}</span>}
                            </span>
                            <span className={styles.itemDesc}>{item.description}</span>
                          </span>
                          <ChevronRight size={16} aria-hidden className={styles.itemChevron} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>
        </div>
      )}
    </div>
  );
}
